/**
 * File: remindersUtils.ts
 * Path: apps/admin/src/features/today/utils/remindersUtils.ts
 * Description: One row of Today's "Reminders to send" box from a reminder
 * the server prepared: what it is in plain words, the ready-made words,
 * the one button that sends it, and why it can't go yet when it can't.
 */
import { ADMIN_PATHS, type ReminderKind, type ReminderProposal } from "@idk/config";

/** What each reminder is, in Liam's words. */
export const REMINDER_WHAT: Record<ReminderKind, string> = {
  beforeDue: "WhatsApp nudge, due in 3 days",
  late3: "Friendly WhatsApp, 3 days late",
  late14: "Firm email with the invoice and statement, 14 days late",
  late30call: "Call them, 30 days late",
};

/** The one button that sends it. */
export const REMINDER_ACTION: Record<ReminderProposal["channel"], string> = {
  whatsapp: "Send on WhatsApp",
  email: "Send email",
  call: "Called",
};

export interface ReminderRow {
  id: string;
  invoiceNumber: string;
  kind: ReminderKind;
  channel: ReminderProposal["channel"];
  party: string;
  what: string;
  balanceCents: number;
  invoiceHref: string;
  /** The client's details, where a missing phone or email is added. Null when the invoice has no client. */
  clientHref: string | null;
  /** The email subject, when it is an email. */
  subject: string | null;
  /** The ready-made words: the WhatsApp text, the email, or the note for the call. */
  words: string;
  /** The wa.me link with the words typed; null for email and call, or with no phone number. */
  whatsappHref: string | null;
  action: string;
  /** Why it can't go as it is; the send button is off while this is set. */
  blocker: string | null;
}

export const reminderRow = (p: ReminderProposal): ReminderRow => ({
  id: p.id,
  invoiceNumber: p.invoiceNumber,
  kind: p.kind,
  channel: p.channel,
  party: p.party,
  what: REMINDER_WHAT[p.kind],
  balanceCents: p.balanceCents,
  invoiceHref: ADMIN_PATHS.invoice(p.invoiceNumber),
  clientHref: p.clientId ? `${ADMIN_PATHS.businesses.detail(p.clientId)}?tab=details` : null,
  subject: p.channel === "email" ? p.emailSubject : null,
  words: (p.channel === "whatsapp" ? p.whatsappText : p.channel === "email" ? p.emailBody : p.callNote) ?? "",
  whatsappHref: p.channel === "whatsapp" ? p.whatsappHref : null,
  action: REMINDER_ACTION[p.channel],
  blocker: p.blocker ?? (p.channel === "whatsapp" && !p.whatsappHref ? `No phone number for ${p.party}.` : null),
});
